import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { leadsApi, aiApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useProduct } from '../context/ProductContext';
import {
  Plus,
  Search,
  Filter,
  Upload,
  MoreVertical,
  Mail,
  Phone,
  Building2,
  Calendar,
  Zap,
  ChevronRight,
  X,
  AlertCircle
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const STAGES = [
  { value: 'new', label: 'New', color: 'bg-slate-100 text-slate-700' },
  { value: 'contacted', label: 'Contacted', color: 'bg-blue-100 text-blue-800' },
  { value: 'booked', label: 'Booked', color: 'bg-purple-100 text-purple-800' },
  { value: 'discovery_done', label: 'Discovery Done', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'proposal_draft', label: 'Proposal Draft', color: 'bg-amber-100 text-amber-800' },
  { value: 'proposal_sent', label: 'Proposal Sent', color: 'bg-orange-100 text-orange-800' },
  { value: 'closed_won', label: 'Won', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'closed_lost', label: 'Lost', color: 'bg-red-100 text-red-800' }
];

const SOURCES = [
  { value: 'manual', label: 'Manual' },
  { value: 'upload', label: 'CSV Upload' },
  { value: 'crm', label: 'CRM Sync' },
  { value: 'form', label: 'Web Form' },
  { value: 'ai_lead_gen', label: 'AI Generated' },
  { value: 'inbound_call', label: 'Inbound Call' }
];

export default function Leads() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedProduct, products } = useProduct();
  
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState(searchParams.get('stage') || '');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [aiLoading, setAiLoading] = useState({});

  useEffect(() => {
    loadLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageFilter, search, selectedProduct]);

  const loadLeads = async () => {
    try {
      setLoading(true);
      const params = {};
      if (stageFilter) params.stage = stageFilter;
      if (search) params.search = search;
      if (selectedProduct && selectedProduct !== 'all') params.product_id = selectedProduct;
      
      const response = await leadsApi.list(params);
      setLeads(response.data);
    } catch (error) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLead = async (data) => {
    try {
      await leadsApi.create(data);
      toast.success('Lead created successfully');
      setShowCreateModal(false);
      loadLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create lead');
    }
  };

  const handleUploadCSV = async (file, defaultProductId) => {
    try {
      const response = await leadsApi.uploadCSV(file, defaultProductId);
      const { imported, failed, errors } = response.data;
      
      if (failed === 0) {
        toast.success(`Successfully imported ${imported} leads`);
      } else {
        toast.warning(`Imported ${imported} leads, ${failed} failed`);
      }
      
      setShowUploadModal(false);
      loadLeads();
    } catch (error) {
      toast.error('Failed to upload CSV');
    }
  };

  const handleTriggerAI = async (leadId, action) => {
    setAiLoading({ ...aiLoading, [leadId]: action });
    try {
      if (action === 'schedule') {
        await aiApi.triggerScheduling(leadId);
        toast.success('Scheduling message sent');
      }
      loadLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI action failed');
    } finally {
      setAiLoading({ ...aiLoading, [leadId]: null });
    }
  };

  const getStageInfo = (stage) => {
    return STAGES.find(s => s.value === stage) || STAGES[0];
  };

  const LeadRow = ({ lead }) => {
    const stageInfo = getStageInfo(lead.stage);
    const isProcessing = aiLoading[lead.id];

    return (
      <tr 
        className="hover:bg-slate-50 cursor-pointer transition-colors"
        onClick={() => navigate(`/leads/${lead.id}`)}
        data-testid={`lead-row-${lead.id}`}
      >
        <td className="px-6 py-4">
          <span className="block font-medium text-slate-900">{lead.name}</span>
          <span className="block text-sm text-slate-500">{lead.email}</span>
        </td>
        <td className="px-6 py-4">
          <span className="flex items-center gap-2 text-slate-600">
            <Building2 className="w-4 h-4" />
            {lead.company}
          </span>
        </td>
        <td className="px-6 py-4">
          {lead.product_name ? (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-indigo-50 text-indigo-700 font-medium">
              {lead.product_name}
            </span>
          ) : (
            <span className="text-xs text-slate-400">—</span>
          )}
        </td>
        <td className="px-6 py-4">
          <span className={`status-badge ${stageInfo.color}`}>
            {stageInfo.label}
          </span>
        </td>
        <td className="px-6 py-4">
          <span className="text-sm text-slate-500 capitalize">
            {lead.source.replace('_', ' ')}
          </span>
        </td>
        <td className="px-6 py-4">
          <span className={`status-badge ${
            lead.consent_status === 'verified' ? 'status-badge-success' : 
            lead.consent_status === 'opted_out' ? 'status-badge-error' : 
            'status-badge-warning'
          }`}>
            {lead.consent_status}
          </span>
        </td>
        <td className="px-6 py-4">
          <span className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {lead.consent_status === 'verified' && 
             lead.stage === 'new' && 
             !lead.opt_out && (
              <Button
                size="sm"
                variant="outline"
                className="gap-1"
                onClick={() => handleTriggerAI(lead.id, 'schedule')}
                disabled={isProcessing}
                data-testid={`ai-schedule-${lead.id}`}
              >
                {isProcessing === 'schedule' ? (
                  <span className="animate-spin">⏳</span>
                ) : (
                  <Zap className="w-4 h-4" />
                )}
                Schedule
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate(`/leads/${lead.id}`)}>
                  View Details
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSelectedLead(lead)}>
                  Edit Lead
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </span>
        </td>
      </tr>
    );
  };

  return (
    <div className="p-8" data-testid="leads-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Leads</h1>
          <p className="text-slate-500 mt-1">Manage your sales pipeline</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => setShowUploadModal(true)}
            data-testid="upload-csv-btn"
          >
            <Upload className="w-4 h-4" />
            Upload CSV
          </Button>
          <Button
            className="btn-primary gap-2"
            onClick={() => setShowCreateModal(true)}
            data-testid="create-lead-btn"
          >
            <Plus className="w-4 h-4" />
            Add Lead
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search leads..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-leads"
              />
            </div>
            <Select value={stageFilter || "all"} onValueChange={(v) => {
              const newValue = v === "all" ? "" : v;
              setStageFilter(newValue);
              if (newValue) {
                setSearchParams({ stage: newValue });
              } else {
                setSearchParams({});
              }
            }}>
              <SelectTrigger className="w-48" data-testid="stage-filter">
                <SelectValue placeholder="All Stages" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                {STAGES.map(stage => (
                  <SelectItem key={stage.value} value={stage.value}>
                    {stage.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Stage
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Consent
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={7} className="px-6 py-4">
                      <div className="animate-pulse h-8 bg-slate-100 rounded"></div>
                    </td>
                  </tr>
                ))
              ) : leads.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No leads found</p>
                    <Button
                      className="mt-4"
                      variant="outline"
                      onClick={() => setShowCreateModal(true)}
                    >
                      Add your first lead
                    </Button>
                  </td>
                </tr>
              ) : (
                leads.map(lead => (
                  <LeadRow key={lead.id} lead={lead} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create Lead Modal */}
      <CreateLeadModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateLead}
      />

      {/* Upload CSV Modal */}
      <UploadCSVModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleUploadCSV}
      />
    </div>
  );
}

// Create Lead Modal Component
function CreateLeadModal({ open, onClose, onSubmit }) {
  const { products, selectedProduct } = useProduct();
  // Default product to ProductContext when on a real product (not 'all' / 'unassigned')
  const defaultProductId =
    selectedProduct && selectedProduct !== 'all' && selectedProduct !== 'unassigned'
      ? selectedProduct
      : '';
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    source: 'manual',
    consent_status: 'pending',
    notes: '',
    product_id: defaultProductId,
    goal: '',
    pain_point: ''
  });
  const [loading, setLoading] = useState(false);

  // Re-sync the product default if the user switches product context with the modal open
  useEffect(() => {
    setFormData((f) => ({ ...f, product_id: f.product_id || defaultProductId }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultProductId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...formData };
      if (!payload.product_id) delete payload.product_id;
      if (!payload.goal) delete payload.goal;
      if (!payload.pain_point) delete payload.pain_point;
      await onSubmit(payload);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        source: 'manual',
        consent_status: 'pending',
        notes: '',
        product_id: '',
        goal: '',
        pain_point: ''
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add New Lead</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="lead-name-input"
                />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="lead-email-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="lead-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="company">Company *</Label>
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  required
                  data-testid="lead-company-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="source">Source</Label>
                <Select
                  value={formData.source}
                  onValueChange={(v) => setFormData({ ...formData, source: v })}
                >
                  <SelectTrigger data-testid="lead-source-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SOURCES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="consent">Consent Status</Label>
                <Select
                  value={formData.consent_status}
                  onValueChange={(v) => setFormData({ ...formData, consent_status: v })}
                >
                  <SelectTrigger data-testid="lead-consent-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="verified">Verified</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="product_id">Product / Service</Label>
              <Select
                value={formData.product_id || 'none'}
                onValueChange={(v) => setFormData({ ...formData, product_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger data-testid="lead-product-select">
                  <SelectValue placeholder="Select product (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Unassigned —</SelectItem>
                  {products.filter(p => p.is_active).map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="goal">Goal</Label>
                <Input
                  id="goal"
                  value={formData.goal}
                  onChange={(e) => setFormData({ ...formData, goal: e.target.value })}
                  placeholder="e.g. save_time"
                  data-testid="lead-goal-input"
                />
              </div>
              <div>
                <Label htmlFor="pain_point">Pain Point</Label>
                <Input
                  id="pain_point"
                  value={formData.pain_point}
                  onChange={(e) => setFormData({ ...formData, pain_point: e.target.value })}
                  placeholder="e.g. manual_follow_up"
                  data-testid="lead-painpoint-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                data-testid="lead-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="btn-primary" disabled={loading} data-testid="submit-lead-btn">
              {loading ? 'Creating...' : 'Create Lead'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Upload CSV Modal Component
function UploadCSVModal({ open, onClose, onUpload }) {
  const { products, selectedProduct } = useProduct();
  const defaultProductFromCtx =
    selectedProduct && selectedProduct !== 'all' && selectedProduct !== 'unassigned'
      ? selectedProduct
      : '';
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [defaultProductId, setDefaultProductId] = useState(defaultProductFromCtx);

  useEffect(() => {
    setDefaultProductId(defaultProductFromCtx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultProductFromCtx]);

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    try {
      await onUpload(file, defaultProductId || undefined);
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload CSV</DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div>
            <Label className="text-xs uppercase text-slate-500">
              Default Product / Service (optional)
            </Label>
            <Select
              value={defaultProductId || 'none'}
              onValueChange={(v) => setDefaultProductId(v === 'none' ? '' : v)}
            >
              <SelectTrigger data-testid="csv-default-product-select">
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Unassigned —</SelectItem>
                {products.filter(p => p.is_active).map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-400 mt-1">
              Applied to rows that do NOT have a <code>product_code</code> column.
            </p>
          </div>
          <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
            {file ? (
              <div className="flex items-center justify-between bg-slate-50 rounded-lg p-4">
                <span className="text-sm text-slate-600">{file.name}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFile(null)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                <Upload className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">
                  Drop your CSV file here or click to browse
                </p>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setFile(e.target.files?.[0])}
                  className="hidden"
                  id="csv-upload"
                  data-testid="csv-file-input"
                />
                <label
                  htmlFor="csv-upload"
                  className="btn-secondary cursor-pointer inline-block"
                >
                  Select File
                </label>
              </>
            )}
          </div>
          <p className="text-xs text-slate-400">
            Required columns: <code>name</code>, <code>email</code>, <code>company</code>.<br />
            Optional columns: <code>phone</code>, <code>product_code</code>, <code>goal</code>, <code>pain_point</code>, <code>notes</code>.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="btn-primary"
            onClick={handleUpload}
            disabled={!file || loading}
            data-testid="upload-csv-submit"
          >
            {loading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
